import { Skeleton } from '@acctglobal/skeleton'
import React, { useState, useEffect, useMemo } from 'react'

import { defaultSort } from '../../../../constants'
import { useRegionalizationContext } from '../../../../contexts/RegionalizationContext'
import { useAnotherScPrices } from '../../../../hooks/useAnotherScPrices'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useProductWithLowStock } from '../../../../hooks/useProductWithLowStock'
import useWindowDimensions from '../../../../hooks/useWindowDimensions'
import { useProductsQuery } from '../../../../sdk/product/useProductsQuery'
import { useSession } from '../../../../sdk/session'
import { SearchProductsCard } from '../SearchProductsCard'

import type { ProductsQueryWithEdges } from '../../../../types/Product'

import './search-products-section.scss'

interface SearchProductsSectionProps {
  title: string
  searchTerm: string
  setOpenInputDropdown: (openDropDown: boolean) => void
}

export const SearchProductsSection = ({
  title,
  searchTerm,
  setOpenInputDropdown,
}: SearchProductsSectionProps) => {
  const [termWithDelay, setTermWithDelay] = useState('')
  const [loading, setLoading] = useState(false)
  const { channel, locale } = useSession()
  const { openRegionModal } = useRegionalizationContext()
  const { isMobile } = useWindowDimensions()

  const debouncedSearchTerm = useDebounce((term: string) => {
    setTermWithDelay(term)
    setLoading(false)
  }, 1000)

  useEffect(() => {
    setLoading(true)
    debouncedSearchTerm(searchTerm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  useEffect(() => {
    if (openRegionModal) {
      setOpenInputDropdown(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRegionModal])

  const {
    data: searchProducts,
    isLoading,
  }: { data: ProductsQueryWithEdges; isLoading: boolean } = useProductsQuery({
    first: 4,
    sort: defaultSort,
    term: termWithDelay ?? '',
    selectedFacets: [
      { key: 'channel', value: channel ?? '' },
      { key: 'locale', value: locale },
    ],
  })

  const searchedProducts = useMemo(
    () => searchProducts?.edges.map((edge) => edge.node),
    [searchProducts]
  )

  const { getAnotherScPrices } = useAnotherScPrices(searchedProducts)
  const { productsWithLowStock, loading: productsWithLowStockLoading } =
    useProductWithLowStock(searchedProducts)

  useEffect(() => {
    setLoading(isLoading)
  }, [isLoading])

  if (loading || productsWithLowStockLoading) {
    return (
      <div className="search-products-section-container">
        <h2 className="search-products-section-title">{title}</h2>

        <Skeleton
          width="100%"
          height={isMobile ? 58 : 80}
          table={{ rows: 4, columns: 1 }}
          backgroundColor="var(--color-grayScale-faded)"
        />
      </div>
    )
  }

  if (
    !productsWithLowStock ||
    (productsWithLowStock && productsWithLowStock.length < 1)
  ) {
    return null
  }

  return (
    <div className="search-products-section-container">
      <h2 className="search-products-section-title">{title}</h2>

      {searchedProducts?.map((product) => {
        const anotherScPrices = getAnotherScPrices(product.sku)

        return (
          <SearchProductsCard
            product={product}
            key={`search-products-card=${product.id}`}
            location="barra de pesquisa search"
            {...anotherScPrices}
          />
        )
      })}
    </div>
  )
}
